/**
 * Lê o CSV que o Guardiões exporta (Console do Admin → Conclusões & Certificações → Exportar CSV).
 * Colunas: Nome, Email, Área, Pontuação, Tentativas, Tempo(min), Data, Código, Origem — o nome vem
 * entre aspas e a Data é "dd/mm/aaaa, hh:mm:ss" SEM aspas (a vírgula dela quebra a coluna em duas).
 */
export const NOTA_MINIMA_GUARDIAO = 80;

export interface LinhaGuardioes {
  nome: string;
  email: string | null;
  area: string | null;
  pontuacao: number;
  /** millis */
  data: number | null;
  codigo: string | null;
}

/** Divide uma linha CSV respeitando aspas ("" = aspas literal). */
export function dividirLinhaCsv(linha: string): string[] {
  const campos: string[] = [];
  let atual = '';
  let entreAspas = false;
  for (let i = 0; i < linha.length; i++) {
    const c = linha[i];
    if (entreAspas) {
      if (c === '"' && linha[i + 1] === '"') {
        atual += '"';
        i++;
      } else if (c === '"') entreAspas = false;
      else atual += c;
    } else if (c === '"') entreAspas = true;
    else if (c === ',' || c === ';') {
      campos.push(atual.trim());
      atual = '';
    } else atual += c;
  }
  campos.push(atual.trim());
  return campos;
}

/** "14/03/2026, 10:22:33" | "14/03/2026 10:22" | "2026-03-14..." → millis. */
export function lerData(texto: string): number | null {
  const t = texto.trim();
  const br = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[,\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (br) {
    const [, d, m, a, h = '0', min = '0', s = '0'] = br;
    return new Date(Number(a), Number(m) - 1, Number(d), Number(h), Number(min), Number(s)).getTime();
  }
  const iso = Date.parse(t);
  return Number.isNaN(iso) ? null : iso;
}

export function lerCsvGuardioes(conteudo: string): LinhaGuardioes[] {
  const linhas = conteudo.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  if (linhas.length === 0) return [];
  const cabecalho = dividirLinhaCsv(linhas[0]).map((c) => c.toLowerCase());
  const temCabecalho = cabecalho.includes('nome') || cabecalho.includes('email');
  const resultado: LinhaGuardioes[] = [];
  for (const linha of temCabecalho ? linhas.slice(1) : linhas) {
    let campos = dividirLinhaCsv(linha);
    // Data "dd/mm/aaaa, hh:mm:ss" sem aspas vira 2 campos — junta de volta (colunas 6 e 7).
    if (campos.length === 10 && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(campos[6]) && /^\d{1,2}:\d{2}/.test(campos[7])) {
      campos = [...campos.slice(0, 6), `${campos[6]}, ${campos[7]}`, ...campos.slice(8)];
    }
    const [nome, email, area, pontuacao, , , data, codigo] = campos;
    if (!nome && !email) continue;
    resultado.push({
      nome: (nome ?? '').trim(),
      email: email?.trim() ? email.trim().toLowerCase() : null,
      area: area?.trim() || null,
      pontuacao: Number(String(pontuacao ?? '').replace(',', '.').replace('%', '')) || 0,
      data: data ? lerData(data) : null,
      codigo: codigo?.trim() || null,
    });
  }
  return resultado;
}

const normalizar = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export interface ColaboradorParaLigar {
  id: string;
  name: string;
  email: string;
}

export type SituacaoImportacao = 'novo' | 'ja_estava' | 'abaixo' | 'sem_cadastro' | 'nome_repetido';

export interface ResultadoImportacao {
  situacao: SituacaoImportacao;
  nome: string;
  email: string | null;
  colaborador: ColaboradorParaLigar | null;
  /** Aprovação mais antiga (≥ nota mínima) — "desde quando é Guardião". */
  aprovadoEm: number | null;
  codigo: string | null;
  melhorNota: number;
}

/**
 * Junta as tentativas por pessoa e decide o que fazer: liga pelo e-mail; sem e-mail, pelo nome
 * (só se houver exatamente um colaborador com esse nome). Selo é pra sempre — quem já é Guardião
 * não sai, mesmo que o CSV novo não o traga.
 */
export function prepararImportacao(
  linhas: LinhaGuardioes[],
  colaboradores: ColaboradorParaLigar[],
  jaGuardioes: Set<string>
): ResultadoImportacao[] {
  const porEmail = new Map(colaboradores.map((c) => [c.email.toLowerCase(), c]));
  const porNome = new Map<string, ColaboradorParaLigar[]>();
  for (const c of colaboradores) {
    const chave = normalizar(c.name);
    porNome.set(chave, [...(porNome.get(chave) ?? []), c]);
  }

  const grupos = new Map<string, LinhaGuardioes[]>();
  for (const linha of linhas) {
    const chave = linha.email ?? `nome:${normalizar(linha.nome)}`;
    grupos.set(chave, [...(grupos.get(chave) ?? []), linha]);
  }

  const resultado: ResultadoImportacao[] = [];
  for (const tentativas of Array.from(grupos.values())) {
    const base = tentativas[0];
    const aprovadas = tentativas.filter((t) => t.pontuacao >= NOTA_MINIMA_GUARDIAO);
    const primeiraAprovacao = aprovadas
      .filter((t) => t.data != null)
      .sort((a, b) => (a.data as number) - (b.data as number))[0] ?? aprovadas[0];
    const melhorNota = Math.max(...tentativas.map((t) => t.pontuacao));

    let colaborador: ColaboradorParaLigar | null = null;
    let semLigacao: SituacaoImportacao | null = null;
    if (base.email) {
      colaborador = porEmail.get(base.email) ?? null;
      if (!colaborador) semLigacao = 'sem_cadastro';
    } else {
      const candidatos = porNome.get(normalizar(base.nome)) ?? [];
      if (candidatos.length === 1) colaborador = candidatos[0];
      else semLigacao = candidatos.length > 1 ? 'nome_repetido' : 'sem_cadastro';
    }

    const situacao: SituacaoImportacao = semLigacao
      ? semLigacao
      : !primeiraAprovacao
        ? 'abaixo'
        : jaGuardioes.has(colaborador!.email.toLowerCase())
          ? 'ja_estava'
          : 'novo';

    resultado.push({
      situacao,
      nome: colaborador?.name ?? base.nome,
      email: colaborador?.email.toLowerCase() ?? base.email,
      colaborador,
      aprovadoEm: primeiraAprovacao?.data ?? null,
      codigo: primeiraAprovacao?.codigo ?? null,
      melhorNota,
    });
  }
  return resultado;
}
